import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isPathbuddyHost } from '../../config/eduHost';
import { STORAGE_KEYS } from '../../config/storageKeys';
import { landingHandoffAPI } from './api/landingHandoffAPI';
import { APIError } from '../../lib/api/APIService';
import { clearAllPendingHandoffState } from './handoffContinuation';

const GREETING = "Tell me what you're prepping for — tap one of the prompts below. LNAT, TMUA, a real interview, whatever's next on your list.";

// Deliberate pause before showing the answer — it already arrives from the
// API instantly, which reads as implausible for something meant to look
// generated. Short enough not to feel slow; matches the prototype's own
// typing-indicator pacing (typingMsg() in docs/design/ivori-chat-prototype.html).
const TYPING_PAUSE_MS = 900;

// Terminal outcome: the token itself is genuinely dead (never existed, or
// expired) — the server returns 404 for both cases (see resume_handoff_route
// in landing_chat.py), so a 404 is the only signal that means "clear it and
// go back to idle." Any other error (network failure, timeout, 5xx) is
// transient — the token may still be perfectly valid, so it must be
// preserved and retried, not discarded.
const isTerminalResumeError = (err) => err instanceof APIError && err.status === 404;

export default function EduLandingChatPrompt({ sectionRef, onHandoffPreservabilityChange }) {
  const navigate = useNavigate();
  const chipTrackRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const assistantName = isPathbuddyHost() ? 'Pathbuddy' : 'IVORI';
  const liveLabel = isPathbuddyHost() ? 'Pathbuddy — live' : 'IVORI Edu — live';

  // 'idle' | 'loading_catalogue' | 'pending' | 'typing' | 'answered' | 'error'
  // 'typing' is a deliberate pause (see TYPING_PAUSE_MS) between the answer
  // arriving from the API and actually showing it — the API itself is
  // instant, which would otherwise make a "generated" answer look implausible.
  const [phase, setPhase] = useState('loading_catalogue');
  const [prompts, setPrompts] = useState([]);
  const [answered, setAnswered] = useState(null); // { promptText, answerText }
  // Set alongside entering 'typing' — the user's own message shows
  // immediately (matching the prototype: only the bot's reply is "typed"),
  // so this needs to be known before `answered` is populated.
  const [pendingPromptText, setPendingPromptText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  // What Retry should actually repeat: 'catalogue' | 'resume' | { promptId }
  const [failedOperation, setFailedOperation] = useState(null);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  const loadCatalogue = async () => {
    try {
      const catalogue = await landingHandoffAPI.getPromptCatalogue('edu');
      setPrompts(catalogue.prompts || []);
      setPhase('idle');
    } catch (err) {
      setErrorMessage('Could not load prompts. Please try again.');
      setFailedOperation({ type: 'catalogue' });
      setPhase('error');
    }
  };

  const resumeFromCachedToken = async (cachedToken) => {
    try {
      const resumed = await landingHandoffAPI.resumeHandoff(cachedToken);
      setAnswered({ promptText: resumed.prompt_text, answerText: resumed.answer_text });
      setPhase('answered');
    } catch (err) {
      if (isTerminalResumeError(err)) {
        // Genuinely dead token (never existed, or expired) — clear it and
        // fall through to a fresh catalogue load.
        clearAllPendingHandoffState();
        await loadCatalogue();
        return;
      }
      // Transient (network/5xx/timeout): preserve the cached token and
      // offer a working retry — do NOT clear it, since it may still be
      // perfectly valid.
      setErrorMessage('Could not restore your session. Please try again.');
      setFailedOperation({ type: 'resume', token: cachedToken });
      setPhase('error');
    }
  };

  const loadCatalogueAndMaybeResume = async () => {
    const cachedToken = sessionStorage.getItem(STORAGE_KEYS.PENDING_HANDOFF_TOKEN);
    if (cachedToken) {
      await resumeFromCachedToken(cachedToken);
      return;
    }
    await loadCatalogue();
  };

  useEffect(() => {
    loadCatalogueAndMaybeResume();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectPrompt = async (promptId) => {
    setPhase('pending');
    setErrorMessage('');
    try {
      const result = await landingHandoffAPI.createHandoff(promptId);

      sessionStorage.setItem(STORAGE_KEYS.PENDING_HANDOFF_TOKEN, result.handoff_token);
      sessionStorage.setItem(STORAGE_KEYS.PENDING_HANDOFF_PROMPT_ID, result.prompt_id);

      // Deliberate pause: the answer already arrived, but showing it
      // instantly reads as implausible for something meant to look
      // generated. Chips stay disabled throughout — 'typing' is treated
      // the same as 'pending' by renderComposer's disabled check below.
      setPendingPromptText(result.prompt_text);
      setPhase('typing');
      typingTimeoutRef.current = setTimeout(() => {
        setAnswered({ promptText: result.prompt_text, answerText: result.answer_text });
        setPhase('answered');
      }, TYPING_PAUSE_MS);
    } catch (err) {
      setErrorMessage('Something went wrong. Please try again.');
      setFailedOperation({ type: 'selectPrompt', promptId });
      setPhase('error');
    }
  };

  const handleRetry = () => {
    setErrorMessage('');
    if (!failedOperation) {
      setPhase('idle');
      return;
    }
    if (failedOperation.type === 'catalogue') {
      setPhase('loading_catalogue');
      loadCatalogue();
      return;
    }
    if (failedOperation.type === 'resume') {
      setPhase('loading_catalogue');
      resumeFromCachedToken(failedOperation.token);
      return;
    }
    if (failedOperation.type === 'selectPrompt') {
      selectPrompt(failedOperation.promptId);
      return;
    }
    setPhase('idle');
  };

  const handleSignMeUp = () => {
    // An anonymous visitor never has a legitimate retry token yet, even
    // when the handoff token/prompt id itself is being preserved.
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_HANDOFF_RETRY_TOKEN);
    navigate('/login?signup=true', { state: { returnTo: '/chat' } });
  };

  // Both the bottom "Sign up free" CTA (below) and the header "Start free"
  // CTA (in EduLandingPage, via onHandoffPreservabilityChange) preserve the
  // handoff whenever THIS component instance has established it's still
  // worth preserving — either a prompt was just answered, or the
  // component's own resume attempt for the cached token failed only
  // transiently (so the token is still presumed valid; phase is 'error'
  // with failedOperation.type === 'resume'). Any other cached token (e.g.
  // one that appeared in sessionStorage without this component ever having
  // attempted to resume it) is not trusted merely by being present, and is
  // cleared by both CTAs — only a resume/selection this instance actually
  // performed vouches for the token's validity.
  const hasPreservableHandoff =
    (phase === 'answered' && Boolean(answered)) ||
    (phase === 'error' && failedOperation?.type === 'resume');

  useEffect(() => {
    if (onHandoffPreservabilityChange) {
      onHandoffPreservabilityChange(hasPreservableHandoff);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPreservableHandoff]);

  const handleSignUpFreeClick = () => {
    if (hasPreservableHandoff) {
      handleSignMeUp();
      return;
    }
    clearAllPendingHandoffState();
    navigate('/login?signup=true', { state: { returnTo: '/chat' } });
  };

  const scrollChips = (direction) => {
    if (chipTrackRef.current) {
      chipTrackRef.current.scrollBy({ left: direction === 'left' ? -220 : 220, behavior: 'smooth' });
    }
  };

  const renderComposerArea = () => {
    if (phase === 'answered' && answered) {
      return (
        <div className="edu-landing-chat-messages">
          <div className="edu-landing-chat-msg edu-landing-user">
            <div className="edu-landing-who">You</div>
            <div className="edu-landing-bubble">{answered.promptText}</div>
          </div>
          <div className="edu-landing-chat-msg edu-landing-assistant">
            <div className="edu-landing-who">{assistantName}</div>
            <div className="edu-landing-bubble">{answered.answerText}</div>
          </div>
        </div>
      );
    }

    if (phase === 'typing') {
      return (
        <div className="edu-landing-chat-messages">
          <div className="edu-landing-chat-msg edu-landing-user">
            <div className="edu-landing-who">You</div>
            <div className="edu-landing-bubble">{pendingPromptText}</div>
          </div>
          <div className="edu-landing-chat-msg edu-landing-assistant" aria-label={`${assistantName} is typing`}>
            <div className="edu-landing-who">{assistantName}</div>
            <div className="edu-landing-chat-typing-dots">
              <span className="edu-landing-typing-dot" />
              <span className="edu-landing-typing-dot" />
              <span className="edu-landing-typing-dot" />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="edu-landing-chat-messages">
        <div className="edu-landing-chat-msg edu-landing-assistant">
          <div className="edu-landing-who">{assistantName}</div>
          <div className="edu-landing-bubble">{GREETING}</div>
        </div>
      </div>
    );
  };

  const renderComposer = () => {
    if (phase === 'answered' && answered) {
      return (
        <div className="edu-landing-chat-input-row">
          <button
            type="button"
            className="edu-landing-sign-me-up-btn"
            onClick={handleSignMeUp}
          >
            Sign me up
          </button>
        </div>
      );
    }

    if (phase === 'error') {
      return (
        <div className="edu-landing-chat-input-row">
          <span className="edu-landing-chat-error-text">{errorMessage}</span>
          <button type="button" className="edu-landing-chat-send" aria-label="Retry" onClick={handleRetry}>
            Retry
          </button>
        </div>
      );
    }

    if (phase === 'typing') {
      // The typing indicator itself already occupies the message area — the
      // chip rail is fully replaced, not just disabled, so nothing implies
      // another prompt could be picked mid-pause.
      return null;
    }

    const disabled = phase === 'pending' || phase === 'loading_catalogue';

    return (
      <div className="edu-landing-chip-slider edu-landing-composer-chip-slider">
        <div className="edu-landing-chip-track" ref={chipTrackRef}>
          {prompts.map((prompt) => (
            <button
              type="button"
              className="edu-landing-chip"
              key={prompt.id}
              disabled={disabled}
              aria-label={`Ask: ${prompt.prompt_text}`}
              onClick={() => selectPrompt(prompt.id)}
            >
              {prompt.label}
            </button>
          ))}
        </div>
        <div className="edu-landing-chip-nav">
          <button
            type="button"
            className="edu-landing-chip-arrow"
            aria-label="Scroll left"
            onClick={() => scrollChips('left')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            className="edu-landing-chip-arrow"
            aria-label="Scroll right"
            onClick={() => scrollChips('right')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  return (
    <section
      className="edu-landing-chat-demo edu-landing-fade-up"
      id="edu-landing-chat-demo"
      ref={sectionRef}
    >
      <div className="edu-landing-chat-window">
        <div className="edu-landing-chat-header">
          <span className="edu-landing-live-dot" />
          <span className="edu-landing-chat-header-label">{liveLabel}</span>
        </div>
        {renderComposerArea()}
        {renderComposer()}
      </div>

      <div className="edu-landing-chat-cta-row">
        <div className="edu-landing-chat-cta-copy">
          Keep going — sign up free to save this session and pick up where you left off.
        </div>
        <button type="button" className="edu-landing-chat-cta-btn" onClick={handleSignUpFreeClick}>
          Sign up free
        </button>
      </div>
    </section>
  );
}
